import React, { useState } from 'react'
import { useCurrency } from '../context/CurrencyContext'
import { EXPENSE_CATEGORIES } from './AddExpenseModal'

const FREQ_TO_MONTHLY = { monthly: 1, quarterly: 1 / 3, 'half-yearly': 1 / 6, yearly: 1 / 12 }

const inputStyle = {
  background: 'var(--input-bg)', border: '1px solid var(--border)',
  borderRadius: '4px', padding: '0.55rem 0.75rem',
  fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif',
  color: 'var(--text)', width: '100%', boxSizing: 'border-box',
  outline: 'none', transition: 'border-color 0.18s'
}

export default function BudgetPlanner({ loans, insurancePolicies, expenses }) {
  const { fmt } = useCurrency()
  const [income, setIncome] = useState(() => localStorage.getItem('dc_budget_income') || '')

  function saveIncome(v) {
    setIncome(v)
    localStorage.setItem('dc_budget_income', v)
  }

  const monthlyIncome = parseFloat(income) || 0
  const totalEMI = loans.reduce((s, l) => s + parseFloat(l.emi_amount || 0), 0)
  const totalInsurance = (insurancePolicies || []).reduce((s, p) => {
    const factor = FREQ_TO_MONTHLY[p.premium_frequency] || 1
    return s + parseFloat(p.premium_amount || 0) * factor
  }, 0)
  const totalExpenses = (expenses || []).reduce((s, e) => {
    const factor = FREQ_TO_MONTHLY[e.frequency] || 1
    return s + parseFloat(e.amount || 0) * factor
  }, 0)
  const totalObligations = totalEMI + totalInsurance + totalExpenses
  const disposable = monthlyIncome - totalObligations
  const dti = monthlyIncome > 0 ? (totalObligations / monthlyIncome) * 100 : 0

  const dtiColor = dti > 50 ? '#b8441a' : dti > 36 ? '#c9a84c' : '#4a6741'
  const dtiLabel = dti > 50 ? 'Danger Zone' : dti > 36 ? 'High' : dti > 0 ? 'Healthy' : '—'

  // 50/30/20 rule
  const needs50 = monthlyIncome * 0.5
  const wants30 = monthlyIncome * 0.3
  const savings20 = monthlyIncome * 0.2

  const Bar = ({ label, amount, max, color, pct: pctOverride }) => {
    const w = max > 0 ? Math.min(100, (amount / max) * 100) : 0
    return (
      <div style={{ marginBottom: '0.85rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.3rem' }}>
          <span style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
          <div style={{ textAlign: 'right' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.92rem', fontWeight: '700', color: 'var(--text)' }}>{monthlyIncome ? fmt(amount) : '—'}</span>
            {pctOverride != null && <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', marginLeft: '0.4rem' }}>({pctOverride.toFixed(1)}%)</span>}
          </div>
        </div>
        <div style={{ height: '8px', background: 'var(--surface-hdr)', borderRadius: '99px', overflow: 'hidden' }}>
          <div style={{ height: '100%', width: w + '%', background: color, borderRadius: '99px', transition: 'width 0.5s ease' }} />
        </div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: '760px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* Income input */}
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
        <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem' }}>
          <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Monthly Income</span>
        </div>
        <div style={{ padding: '1.5rem' }}>
          <label style={{ display: 'block', fontFamily: 'monospace', fontSize: '0.67rem', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>
            Net Monthly Income (₹)
          </label>
          <input
            style={{ ...inputStyle, maxWidth: '320px' }}
            type="text" inputMode="decimal" placeholder="e.g. 80000"
            value={income} onChange={e => saveIncome(e.target.value)}
            onFocus={e => e.target.style.borderColor = 'var(--text)'}
            onBlur={e => e.target.style.borderColor = 'var(--border)'}
          />
          {!income && (
            <div style={{ fontFamily: 'monospace', fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>Enter your monthly take-home pay to see budget breakdown</div>
          )}
        </div>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
        {[
          { label: 'Monthly EMI',       value: totalEMI,       color: '#b8441a', sub: `${loans.length} loan${loans.length !== 1 ? 's' : ''}` },
          { label: 'Insurance / Month', value: totalInsurance,  color: '#3d4a5c', sub: `${(insurancePolicies || []).length} polic${(insurancePolicies || []).length !== 1 ? 'ies' : 'y'}` },
          { label: 'Monthly Expenses',  value: totalExpenses,   color: '#c9a84c', sub: `${(expenses || []).length} item${(expenses || []).length !== 1 ? 's' : ''}` },
          { label: 'Disposable Income', value: disposable,      color: disposable >= 0 ? '#4a6741' : '#b8441a', sub: monthlyIncome > 0 ? `${(100 - dti).toFixed(1)}% of income` : 'set income above' },
        ].map(c => (
          <div key={c.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.1rem 1.3rem', position: 'relative', overflow: 'hidden' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: c.color }} />
            <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: '0.35rem' }}>{c.label}</div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.4rem', fontWeight: '700', color: c.color }}>{fmt(c.value)}</div>
            <div style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>{c.sub}</div>
          </div>
        ))}
      </div>

      {/* DTI gauge */}
      {monthlyIncome > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', padding: '1.25rem 1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.6rem' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Expense-to-Income Ratio</span>
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.5rem', fontWeight: '900', color: dtiColor }}>{dti.toFixed(1)}%</span>
              <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', marginLeft: '0.4rem', color: dtiColor, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{dtiLabel}</span>
            </div>
          </div>
          <div style={{ height: '10px', background: 'var(--surface-hdr)', borderRadius: '99px', overflow: 'hidden', marginBottom: '0.5rem' }}>
            <div style={{ height: '100%', width: Math.min(100, dti) + '%', background: dtiColor, borderRadius: '99px', transition: 'width 0.5s' }} />
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)' }}>
            <span style={{ color: '#4a6741' }}>● Below 36% — Healthy</span>
            <span style={{ color: '#c9a84c' }}>● 36–50% — High</span>
            <span style={{ color: '#b8441a' }}>● Above 50% — Danger</span>
          </div>
          <div style={{ display: 'flex', gap: '0.6rem', fontFamily: 'monospace', fontSize: '0.61rem', color: 'var(--text-muted)', marginTop: '0.5rem', flexWrap: 'wrap' }}>
            <span>EMIs: <span style={{ color: '#b8441a', fontWeight: '600' }}>{fmt(totalEMI)}</span></span>
            <span>+</span>
            <span>Insurance: <span style={{ color: '#3d4a5c', fontWeight: '600' }}>{fmt(totalInsurance)}</span></span>
            <span>+</span>
            <span>Expenses: <span style={{ color: '#c9a84c', fontWeight: '600' }}>{fmt(totalExpenses)}</span></span>
            <span>= <span style={{ color: 'var(--text)', fontWeight: '600' }}>{fmt(totalObligations)}</span></span>
          </div>
          {dti > 50 && (
            <div style={{ marginTop: '0.75rem', background: 'rgba(184,68,26,0.08)', border: '1px solid rgba(184,68,26,0.2)', borderRadius: '6px', padding: '0.7rem 1rem', fontFamily: 'monospace', fontSize: '0.72rem', color: '#b8441a', lineHeight: 1.5 }}>
              ⚠ Your total obligations (EMIs + insurance + expenses) exceed 50% of income. Consider reducing discretionary expenses or paying off smaller loans first to free up cash flow.
            </div>
          )}
        </div>
      )}

      {/* 50/30/20 Rule */}
      {monthlyIncome > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem', display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>50/30/20 Budget Rule</span>
            <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)' }}>Needs / Wants / Savings</span>
          </div>
          <div style={{ padding: '1.25rem 1.5rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.25rem' }}>
              {[
                { label: '50% Needs', ideal: needs50, color: '#3d4a5c', note: 'Rent, EMIs, essentials' },
                { label: '30% Wants', ideal: wants30, color: '#c9a84c', note: 'Lifestyle, dining, entertainment' },
                { label: '20% Savings', ideal: savings20, color: '#4a6741', note: 'Investments, emergency fund' },
              ].map(c => (
                <div key={c.label} style={{ background: 'var(--surface-hdr)', borderRadius: '6px', padding: '0.85rem 1rem', borderTop: `3px solid ${c.color}` }}>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.3rem' }}>{c.label}</div>
                  <div style={{ fontFamily: 'Georgia,serif', fontSize: '1.1rem', fontWeight: '700', color: c.color }}>{fmt(c.ideal)}</div>
                  <div style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'var(--text-muted)', marginTop: '0.2rem', lineHeight: 1.4 }}>{c.note}</div>
                </div>
              ))}
            </div>
            <Bar label="Loan EMIs" amount={totalEMI} max={monthlyIncome} color="#b8441a" pct={monthlyIncome > 0 ? (totalEMI / monthlyIncome) * 100 : 0} />
            <Bar label="Insurance Premiums" amount={totalInsurance} max={monthlyIncome} color="#3d4a5c" pct={monthlyIncome > 0 ? (totalInsurance / monthlyIncome) * 100 : 0} />
            <Bar label="Living Expenses" amount={totalExpenses} max={monthlyIncome} color="#c9a84c" pct={monthlyIncome > 0 ? (totalExpenses / monthlyIncome) * 100 : 0} />
            <div style={{ height: '1px', background: 'var(--border)', margin: '0.4rem 0 0.85rem' }} />
            <Bar label="Total Obligations" amount={totalObligations} max={monthlyIncome} color={dtiColor} pct={dti} />
            <Bar label="50% Needs Budget" amount={needs50} max={monthlyIncome} color="#3d4a5c" pct={50} />
            <div style={{ fontFamily: 'monospace', fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.25rem', lineHeight: 1.5 }}>
              {totalObligations <= needs50
                ? `✓ Your total obligations (${fmt(totalObligations)}) fit within the 50% needs budget (${fmt(needs50)}).`
                : `⚠ Your total obligations (${fmt(totalObligations)}) exceed the 50% needs budget (${fmt(needs50)}) by ${fmt(totalObligations - needs50)}.`}
            </div>
          </div>
        </div>
      )}

      {/* EMI breakdown */}
      {loans.length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>EMI Breakdown</span>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '700', color: '#b8441a' }}>{fmt(totalEMI)}<span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', fontWeight: '400' }}>/mo</span></span>
          </div>
          <div style={{ padding: '1.25rem 1.5rem' }}>
            {loans.map(loan => {
              const emi = parseFloat(loan.emi_amount || 0)
              const share = totalEMI > 0 ? (emi / totalEMI) * 100 : 0
              return (
                <div key={loan.id} style={{ marginBottom: '0.8rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.25rem' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text)' }}>{loan.name}</span>
                    <div>
                      <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.85rem', fontWeight: '700', color: 'var(--text)' }}>{fmt(emi)}</span>
                      <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', marginLeft: '0.35rem' }}>{share.toFixed(1)}%</span>
                    </div>
                  </div>
                  <div style={{ height: '5px', background: 'var(--surface-hdr)', borderRadius: '99px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: share + '%', background: '#b8441a', borderRadius: '99px', transition: 'width 0.5s' }} />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Expenses breakdown by category */}
      {(expenses || []).length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px', overflow: 'hidden' }}>

          {/* Header */}
          <div style={{ background: 'var(--surface-hdr)', borderBottom: '1px solid var(--border)', padding: '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div>
              <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.05rem', fontWeight: '700' }}>Expenses Breakdown</span>
              <span style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: 'var(--text-muted)', marginLeft: '0.6rem' }}>
                {(expenses || []).length} item{(expenses || []).length !== 1 ? 's' : ''} across {new Set((expenses || []).map(e => e.category)).size} categor{new Set((expenses || []).map(e => e.category)).size !== 1 ? 'ies' : 'y'}
              </span>
            </div>
            <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.95rem', fontWeight: '700', color: '#c9a84c' }}>
              {fmt(totalExpenses)}
              <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', fontWeight: '400', marginLeft: '0.2rem' }}>/mo</span>
            </span>
          </div>

          {/* Category groups */}
          {EXPENSE_CATEGORIES.map(cat => {
            const catItems = (expenses || []).filter(e => e.category === cat.value)
            if (catItems.length === 0) return null
            const catTotal = catItems.reduce((s, e) => s + parseFloat(e.amount || 0) * (FREQ_TO_MONTHLY[e.frequency] || 1), 0)
            const share = totalExpenses > 0 ? (catTotal / totalExpenses) * 100 : 0

            return (
              <div key={cat.value} style={{ borderBottom: '1px solid var(--border)' }}>

                {/* Category header row */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.7rem 1.5rem', background: 'var(--surface-hdr)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.95rem', lineHeight: 1 }}>{cat.icon}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: cat.color }}>{cat.label}</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)' }}>
                      {catItems.length} item{catItems.length !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {/* Mini stacked bar */}
                    <div style={{ width: '80px', height: '4px', background: 'var(--border)', borderRadius: '99px', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: share + '%', background: cat.color, borderRadius: '99px', transition: 'width 0.5s' }} />
                    </div>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.6rem', color: 'var(--text-muted)', minWidth: '32px', textAlign: 'right' }}>{share.toFixed(1)}%</span>
                    <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: '700', color: cat.color, minWidth: '72px', textAlign: 'right' }}>{fmt(catTotal)}</span>
                  </div>
                </div>

                {/* Individual items */}
                {catItems.map((exp, idx) => {
                  const monthly = parseFloat(exp.amount || 0) * (FREQ_TO_MONTHLY[exp.frequency] || 1)
                  const isLast = idx === catItems.length - 1
                  return (
                    <div key={exp.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.55rem 1.5rem 0.55rem 2.6rem', borderBottom: isLast ? 'none' : '1px solid var(--surface-hdr)', transition: 'background 0.12s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(201,168,76,0.03)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
                        <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: cat.color, flexShrink: 0, opacity: 0.6 }} />
                        <span style={{ fontFamily: 'monospace', fontSize: '0.72rem', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{exp.name}</span>
                        <span style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'var(--text-muted)', background: 'var(--surface-hdr)', padding: '0.08rem 0.35rem', borderRadius: '3px', flexShrink: 0, textTransform: 'capitalize' }}>{exp.frequency}</span>
                        {exp.due_day && (
                          <span style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'var(--text-muted)', flexShrink: 0 }}>due {exp.due_day}{['st','nd','rd'][((exp.due_day + 90) % 100 - 10 % 10) - 1] || 'th'}</span>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: '1rem' }}>
                        <span style={{ fontFamily: 'Georgia,serif', fontSize: '0.85rem', fontWeight: '700', color: 'var(--text)' }}>{fmt(exp.amount)}</span>
                        {exp.frequency !== 'monthly' && (
                          <div style={{ fontFamily: 'monospace', fontSize: '0.58rem', color: 'var(--text-muted)' }}>≈ {fmt(monthly)}/mo</div>
                        )}
                      </div>
                    </div>
                  )
                })}

              </div>
            )
          })}

          {/* Grand total footer */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.9rem 1.5rem', background: 'var(--surface-hdr)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '0.67rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>
                Grand Total
              </span>
              {/* Stacked colour bar */}
              <div style={{ width: '120px', height: '6px', background: 'var(--border)', borderRadius: '99px', overflow: 'hidden', display: 'flex', gap: '1px' }}>
                {EXPENSE_CATEGORIES.map(cat => {
                  const catT = (expenses || []).filter(e => e.category === cat.value).reduce((s, e) => s + parseFloat(e.amount || 0) * (FREQ_TO_MONTHLY[e.frequency] || 1), 0)
                  const w = totalExpenses > 0 ? (catT / totalExpenses) * 100 : 0
                  if (w < 0.5) return null
                  return <div key={cat.value} style={{ height: '100%', width: w + '%', background: cat.color, flexShrink: 0 }} title={cat.label} />
                })}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem' }}>
              {monthlyIncome > 0 && (
                <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)' }}>
                  {((totalExpenses / monthlyIncome) * 100).toFixed(1)}% of income
                </span>
              )}
              <span style={{ fontFamily: 'Georgia,serif', fontSize: '1.2rem', fontWeight: '900', color: '#c9a84c' }}>
                {fmt(totalExpenses)}
                <span style={{ fontFamily: 'monospace', fontSize: '0.62rem', color: 'var(--text-muted)', fontWeight: '400', marginLeft: '0.2rem' }}>/mo</span>
              </span>
            </div>
          </div>

        </div>
      )}
    </div>
  )
}
